// src/components/CountryCodeSelect.jsx
import React, { useMemo } from 'react';
import { Autocomplete, TextField, Avatar, Box, CircularProgress } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useCountryCodes } from '../hooks/useCountryCodes';

const flagEmoji = (countryCode) => {
  if (!countryCode) return '🏳️';
  return countryCode
    .toUpperCase()
    .replace(/./g, (ch) => String.fromCodePoint(127397 + ch.charCodeAt()));
};

const CountryCodeSelect = ({
  value,
  onChange,
  error,
  helperText,
  disabled = false,
  sx,
  label = 'Código',
  textFieldProps = {},
}) => {  
  
  const theme = useTheme();
  const { countryCodes, loading } = useCountryCodes();

  const selectedOption = useMemo(() => {
    if (!value) return null;
    return countryCodes.find((opt) => opt.code === value) || null;
  }, [value, countryCodes]);

  return (
    <Autocomplete
      options={countryCodes}
      value={selectedOption}
      onChange={(_, newValue) => onChange(newValue ? newValue.code : '')}
      getOptionLabel={(option) =>
        option?.code ? `${option.code} ${option.country || ''}` : ''
      }  
      loading={loading}
      loadingText="Cargando códigos…"
      noOptionsText={loading ? 'Cargando…' : 'Sin resultados'}
      disabled={disabled}
      sx={sx}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          fullWidth
          margin="normal"
          size="medium"
          error={!!error}
          helperText={helperText || ' '}
          autoComplete={textFieldProps?.autoComplete || 'tel-country-code'} 
          {...textFieldProps}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                <Avatar
                  sx={{
                    width: 20,
                    height: 20,
                    fontSize: 12,
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: theme.palette.primary.main,
                    fontWeight: 700,
                    mr: 1,
                  }}
                >
                  {flagEmoji(selectedOption?.countryCode) || '●'}
                </Avatar>
                {params.InputProps.startAdornment}
              </Box>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      // Usa <li> nativo para que React asigne correctamente el key sin warnings
      renderOption={(props, option) => (
        <li {...props}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar
              sx={{
                width: 20,
                height: 20,
                fontSize: 12,
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.main,
                fontWeight: 700,
                mr: 1,
              }}
            >
              {flagEmoji(option.countryCode)}
            </Avatar>
            {option.code} — {option.country}
          </Box>
        </li>
      )}
      isOptionEqualToValue={(opt, val) => opt?.code === val?.code}
    />
  );
};

export default CountryCodeSelect;
